import React, { createContext, useContext } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { Permission, Role, AccessScope } from '@/types/domain/user';
import { hasPermissionInRoles, isPortalAllowedForRole } from '@/config/permissions';
import { PortalContext } from '@/config/navigation.config';

interface AuthorizationContextType {
  can: (permission: Permission) => boolean;
  canAll: (permissions: Permission[]) => boolean;
  canAny: (permissions: Permission[]) => boolean;
  hasRole: (roles: Role | Role[]) => boolean;
  canAccessPortal: (portal: PortalContext) => boolean;
  canAccessScope: (target: Partial<AccessScope>) => boolean;
  canApproveAmount: (amount: number) => boolean;
  isAuditorReadOnly: () => boolean;
}

const AuthorizationContext = createContext<AuthorizationContextType | undefined>(undefined);

export const AuthorizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  const can = (permission: Permission): boolean => {
    if (!user || user.status !== 'ACTIVE') return false; // Fail closed / Deny by default
    if (user.role === 'ADMIN') return true;
    if (user.permissions && (user.permissions.includes(permission) || user.permissions.includes('ALL'))) return true;
    return hasPermissionInRoles(user.roles || [user.role], permission);
  };

  const canAll = (permissions: Permission[]): boolean => {
    return permissions.every((p) => can(p));
  };

  const canAny = (permissions: Permission[]): boolean => {
    return permissions.some((p) => can(p));
  };

  const hasRole = (roles: Role | Role[]): boolean => {
    if (!user || user.status !== 'ACTIVE') return false;
    const targetRoles = Array.isArray(roles) ? roles : [roles];
    const userRoles = user.roles || [user.role];
    return targetRoles.some((r) => userRoles.includes(r));
  };

  const canAccessPortal = (portal: PortalContext): boolean => {
    if (!user || user.status !== 'ACTIVE') return false;
    if (user.role === 'ADMIN') return true;
    return isPortalAllowedForRole(user.role, portal);
  };

  const canAccessScope = (target: Partial<AccessScope>): boolean => {
    if (!user || user.status !== 'ACTIVE') return false;
    if (user.role === 'ADMIN') return true;

    const userScope = user.accessScope;
    if (!userScope) return false;

    if (userScope.ownership === 'ENTERPRISE') return true;

    if (target.departments && target.departments.length > 0) {
      const hasDept = target.departments.some((d) => userScope.departments.includes(d));
      if (!hasDept) return false;
    }

    if (target.plants && target.plants.length > 0) {
      const hasPlant = target.plants.some((p) => userScope.plants.includes(p));
      if (!hasPlant) return false;
    }

    return true;
  };

  const canApproveAmount = (amount: number): boolean => {
    if (!user || user.status !== 'ACTIVE') return false;
    if (user.role === 'ADMIN') return true;
    if (!can('REQUEST_APPROVE') && !can('NFA_APPROVE') && !can('PO_APPROVE')) return false;
    return (user.approvalAuthorityLimit || 0) >= amount;
  };

  const isAuditorReadOnly = (): boolean => {
    return user?.role === 'AUDITOR';
  };

  return (
    <AuthorizationContext.Provider
      value={{
        can,
        canAll,
        canAny,
        hasRole,
        canAccessPortal,
        canAccessScope,
        canApproveAmount,
        isAuditorReadOnly,
      }}
    >
      {children}
    </AuthorizationContext.Provider>
  );
};

export function useAuthorization() {
  const context = useContext(AuthorizationContext);
  if (!context) {
    throw new Error('useAuthorization must be used within an AuthorizationProvider');
  }
  return context;
}
